// ============================================================================
// ToastProvider - the shared staff-side toast system.
//
// Mounted once at the app root (main.jsx). Any component can call useToast():
//
//   const { toast, dismiss } = useToast();
//   toast("Saved");                                  // simple success
//   toast({ kind: "error", text: "Could not save" });
//   toast({ text: "Task completed", action: { label: "Undo", onClick: reopen } });
//
// Kinds: success (alias "ok"), error, warn (alias "warning"), info. A toast
// with an action gets a longer default lifetime so there's time to act. Toasts
// float bottom-center, stack, auto-dismiss, and can be closed manually. This is
// the primitive undo (and, over time, the ad-hoc per-view toasts) build on.
// ============================================================================
import { createContext, useContext, useState, useCallback, useRef, useEffect } from "react";
import { C } from "../lib/tokens";
import { createPortal } from "react-dom";
import { ConfirmDialogHost } from "./ConfirmDialog";
import useIsMobile from "../hooks/useIsMobile";

const ToastCtx = createContext(null);

// Imperative escape hatch: a module-level toast() that dispatches to the mounted
// provider, so any code (including modals / sub-components / callbacks that
// aren't easy to reach with the hook) can raise a toast without useToast().
// Same signature as the hook's toast. The provider registers its emitter below.
let emit = null;
export function toast(opts) {
  if (emit) return emit(opts);
  if (typeof console !== "undefined") console.warn("toast() called before ToastProvider mounted");
  return null;
}

const PALETTE = {
  success: { bg: C.successBg, fg: C.successFg, bar: C.successSolid },
  error:   { bg: C.dangerBg, fg: C.dangerFg, bar: C.dangerSolid },
  warn:    { bg: C.warnBg, fg: C.warnFg, bar: C.warnSolid },
  info:    { bg: C.infoBg, fg: C.infoFg, bar: C.infoSolid },
};

function normalizeKind(kind) {
  if (kind === "ok") return "success";
  if (kind === "warning") return "warn";
  return PALETTE[kind] ? kind : "success";
}

// One-time keyframe injection (inline styles cannot express @keyframes).
if (typeof document !== "undefined" && !document.getElementById("pos-toast-style")) {
  const el = document.createElement("style");
  el.id = "pos-toast-style";
  el.textContent = "@keyframes pos-toast-in{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:none}}";
  document.head.appendChild(el);
}

let nextId = 0;

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timers = useRef({});
  // Undo is now the everyday safety net (it replaced most confirm boxes), so
  // its button must be a real 44px touch target on phones.
  const isMobile = useIsMobile();

  const dismiss = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    if (timers.current[id]) {
      clearTimeout(timers.current[id]);
      delete timers.current[id];
    }
  }, []);

  const showToast = useCallback((opts) => {
    const o = typeof opts === "string" ? { text: opts } : (opts || {});
    const id = ++nextId;
    const kind = normalizeKind(o.kind);
    const duration = o.duration != null ? o.duration : (o.action ? 6000 : 4000);
    // Accepts { text } or { title, body }. Until Sep 2026 only `text` was read,
    // so title/body callers (mock audit, practice voice, No answer) showed an
    // EMPTY toast. Either shape now renders.
    setToasts((prev) => [...prev, { id, kind, text: o.text || "", title: o.title || "", body: o.body || "", action: o.action || null }]);
    if (duration > 0) timers.current[id] = setTimeout(() => dismiss(id), duration);
    return id;
  }, [dismiss]);

  // Register the module-level emitter so the imperative toast() works.
  useEffect(() => {
    emit = showToast;
    return () => { if (emit === showToast) emit = null; };
  }, [showToast]);

  return (
    <ToastCtx.Provider value={{ toast: showToast, dismiss }}>
      {children}
      {/* Host for the imperative confirmDialog() that replaced window.confirm
          (Round 3 #4). Mounted here because this provider already wraps the
          whole staff app once. */}
      <ConfirmDialogHost />
      {/* Portalled: a transformed ancestor would clip the toast layer. */}
      {typeof document !== "undefined" && createPortal(
      <div className="pos-no-print" style={{
        position: "fixed", left: 0, right: "var(--assistant-inset, 0px)", bottom: 0, zIndex: 4000,
        display: "flex", flexDirection: "column", alignItems: "center",
        gap: 8, padding: 16, pointerEvents: "none",
      }}>
        {toasts.map((t) => {
          const p = PALETTE[t.kind];
          return (
            <div key={t.id} role="status" style={{
              pointerEvents: "auto",
              display: "flex", alignItems: "center", gap: 12,
              minWidth: 280, maxWidth: 460,
              padding: "10px 12px 10px 14px",
              borderRadius: 10,
              background: p.bg, color: p.fg,
              boxShadow: "0 6px 24px rgba(0,0,0,0.14)",
              borderLeft: "3px solid " + p.bar,
              fontSize: 13,
              animation: "pos-toast-in 0.18s ease-out",
            }}>
              <span style={{ flex: 1, lineHeight: 1.35 }}>
                {t.title && <strong style={{ display: "block", fontWeight: 700 }}>{t.title}</strong>}
                {t.body && <span style={{ display: "block", opacity: 0.9 }}>{t.body}</span>}
                {t.text}
              </span>
              {t.action && (
                <button
                  onClick={() => { try { t.action.onClick(); } finally { dismiss(t.id); } }}
                  style={{
                    background: "transparent", border: "1px solid " + p.bar,
                    color: p.fg, fontWeight: 700, fontSize: 12,
                    padding: "3px 10px", borderRadius: 6, cursor: "pointer",
                    flexShrink: 0, fontFamily: "inherit", minHeight: isMobile ? 44 : 28, minWidth: isMobile ? 64 : 0,
                  }}>{t.action.label || "Undo"}</button>
              )}
              <button onClick={() => dismiss(t.id)} aria-label="Dismiss" style={{
                background: "transparent", border: "none", color: p.fg,
                cursor: "pointer", fontSize: 16, lineHeight: 1, flexShrink: 0, opacity: 0.6,
              }}>&times;</button>
            </div>
          );
        })}
      </div>,
      document.body)}
    </ToastCtx.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastCtx);
  // No-op fallback so a component still renders if used outside the provider.
  return ctx || { toast: () => {}, dismiss: () => {} };
}
